import { getLenis } from '../modules/lenis';
import { PartnerMapPopup } from './PartnerMapPopup';

export class GoogleMapsPartner {
    static MARKER_BACKGROUND_FALLBACK = '#ff624c';
    static MARKER_BORDER_FALLBACK = '#e85945';
    static MARKER_GLYPH_FALLBACK = '#ffffff';

    constructor(mapEl) {
        if (!mapEl) return;

        this.mapWrapper = mapEl;
        this.init();
    }

    init() {
        this.mapInstance = null;
        this.markers = [];
        this.activeMarker = null;
        this.PinElement = null;
        this.mapClickListener = null;
        this.mapDragListener = null;
        this.partnerPopup = null;
        this.projectionOverlay = null;
        this.ignoreMapClickUntil = 0;
        this.lenisPausedByMap = false;
        this.onEscapeKey = this.onEscapeKey.bind(this);
        this.onMapWheel = this.onMapWheel.bind(this);
        this.onMapPointerEnter = this.onMapPointerEnter.bind(this);
        this.onMapPointerLeave = this.onMapPointerLeave.bind(this);

        const mapShell =
            this.mapWrapper?.closest('[js-map-shell]') ??
            this.mapWrapper ??
            null;
        this.mapShell = mapShell;
        this.mapShell?.setAttribute('data-lenis-prevent', '');
        this.mapWrapper?.setAttribute('data-lenis-prevent', '');
        const popupEl = mapShell?.querySelector('[js-map-popup]') ?? null;

        if (popupEl) {
            this.partnerPopup = new PartnerMapPopup(popupEl, {
                onClose: () => this.clearActiveMarker(),
            });
        }
    }

    getCssColor(variable, fallback) {
        const value = getComputedStyle(document.documentElement)
            .getPropertyValue(variable)
            .trim();

        return value || fallback;
    }

    getMarkerColors() {
        const background =
            this.mapWrapper?.dataset?.markerBackground ||
            this.getCssColor(
                '--color-background-accent',
                GoogleMapsPartner.MARKER_BACKGROUND_FALLBACK
            );
        const border =
            this.mapWrapper?.dataset?.markerBorder ||
            this.getCssColor(
                '--color-orange-600',
                GoogleMapsPartner.MARKER_BORDER_FALLBACK
            );
        const glyph =
            this.mapWrapper?.dataset?.markerGlyph ||
            GoogleMapsPartner.MARKER_GLYPH_FALLBACK;

        return { background, border, glyph };
    }

    createMarkerPin(point = null) {
        const pinUrl = point?.type?.pin_url || point?.pin_url || '';

        if (pinUrl) {
            const wrapper = document.createElement('button');
            wrapper.type = 'button';
            wrapper.className =
                'partner-map-pin block cursor-pointer border-0 bg-transparent p-0 transition-transform duration-200 ease-out will-change-transform';
            wrapper.setAttribute('aria-label', point?.title || 'Partner');

            const img = document.createElement('img');
            img.src = pinUrl;
            img.alt = '';
            img.width = 30;
            img.height = 38;
            img.draggable = false;
            img.className =
                'partner-map-pin__img block h-[38px] w-[30px] object-contain drop-shadow-sm pointer-events-none';

            wrapper.appendChild(img);
            return { element: wrapper, isCustom: true };
        }

        if (!this.PinElement) {
            return null;
        }

        const { background, border, glyph } = this.getMarkerColors();

        return {
            element: new this.PinElement({
                background,
                borderColor: border,
                glyphColor: glyph,
            }).element,
            isCustom: false,
        };
    }

    setMarkerActiveState(marker, isActive) {
        const content = marker?.content;
        if (!(content instanceof HTMLElement)) return;

        content.classList.toggle('is-active', isActive);
        content.classList.toggle('scale-110', isActive);
        content.style.zIndex = isActive ? '999' : '';
        if (marker) {
            marker.zIndex = isActive ? 999 : null;
        }
    }

    parsePoints(rawPoints = null) {
        if (Array.isArray(rawPoints)) {
            return rawPoints;
        }

        try {
            const points = JSON.parse(
                this.mapWrapper.getAttribute('data-point') || '[]'
            );
            return Array.isArray(points) ? points : [];
        } catch (error) {
            return [];
        }
    }

    clearMarkers() {
        this.clearActiveMarker();

        this.markers.forEach(item => {
            if (item?.marker) {
                item.marker.map = null;
            }
        });

        this.markers = [];
    }

    createMarkers(points = []) {
        this.clearMarkers();

        points.forEach(point => {
            const lat = parseFloat(point.lat);
            const lng = parseFloat(point.lng);

            if (Number.isNaN(lat) || Number.isNaN(lng)) {
                return;
            }

            const pin = this.createMarkerPin(point);

            const MarkerElement =
                this.AdvancedMarkerElement ||
                google.maps.marker?.AdvancedMarkerElement;

            if (!MarkerElement) {
                return;
            }

            const marker = new MarkerElement({
                map: this.mapInstance,
                position: { lat, lng },
                title: point.title,
                gmpClickable: true,
                ...(pin ? { content: pin.element } : {}),
            });

            marker.addEventListener('click', () => {
                this.onMarkerClick(marker);
            });

            this.markers.push({
                id: point.id,
                marker,
                data: point,
            });
        });
    }

    fitToMarkers(padding = 48) {
        if (!this.mapInstance || !this.markers.length) {
            return;
        }

        if (this.markers.length === 1) {
            const position = this.markers[0].marker.position;
            this.mapInstance.setCenter(position);
            this.mapInstance.setZoom(14);
            return;
        }

        const bounds = new google.maps.LatLngBounds();

        this.markers.forEach(item => {
            const position = item.marker.position;
            if (!position) return;

            bounds.extend(position);
        });

        this.mapInstance.fitBounds(bounds, padding);
    }

    async setPoints(points = [], options = {}) {
        const { activeId = null, fit = true } = options;
        const nextPoints = Array.isArray(points) ? points : [];

        this.mapWrapper?.setAttribute('data-point', JSON.stringify(nextPoints));

        if (!this.mapInstance) {
            await this.initMap(activeId, nextPoints);
            return;
        }

        this.createMarkers(nextPoints);

        if (fit) {
            this.fitToMarkers();
        }

        // Single result: open pin. Multiple: keep overview (fitBounds).
        if (activeId && nextPoints.length === 1) {
            const item = this.markers.find(m => m.id == activeId);
            if (item) {
                item.marker.click();
            }
        }
    }

    async initMap(id = null, rawPoints = null) {
        this.teardownMapListeners();

        await google.maps.importLibrary('maps');
        const { AdvancedMarkerElement, PinElement } =
            await google.maps.importLibrary('marker');

        this.PinElement = PinElement;
        this.AdvancedMarkerElement = AdvancedMarkerElement;

        const points = this.parsePoints(rawPoints);
        if (!points?.length) {
            this.clearMarkers();
            return;
        }

        const firstPoint = points[0];

        this.mapInstance = new google.maps.Map(this.mapWrapper, {
            zoom: 14,
            center: {
                lat: parseFloat(firstPoint.lat),
                lng: parseFloat(firstPoint.lng),
            },
            mapId: 'map',
            gestureHandling: 'greedy',
        });

        this.partnerPopup?.setMap(this.mapInstance);
        this.createMarkers(points);
        this.fitToMarkers();
        this.bindMapListeners();

        // Only auto-open a pin when there is exactly one point.
        if (id && points.length === 1) {
            const item = this.markers.find(m => m.id == id);
            if (item) {
                item.marker.click();
            }
        }
    }

    bindMapListeners() {
        this.mapClickListener = this.mapInstance.addListener('click', () => {
            if (Date.now() < this.ignoreMapClickUntil) return;
            this.clearActiveMarker();
        });

        this.mapDragListener = this.mapInstance.addListener('dragstart', () => {
            if (Date.now() < this.ignoreMapClickUntil) return;
            this.clearActiveMarker();
        });

        this.mapShell?.addEventListener('pointerenter', this.onMapPointerEnter);
        this.mapShell?.addEventListener('pointerleave', this.onMapPointerLeave);
        this.mapShell?.addEventListener('wheel', this.onMapWheel, {
            passive: false,
            capture: true,
        });
        document.addEventListener('keydown', this.onEscapeKey);
    }

    teardownMapListeners() {
        if (this.mapClickListener) {
            google.maps.event.removeListener(this.mapClickListener);
            this.mapClickListener = null;
        }

        if (this.mapDragListener) {
            google.maps.event.removeListener(this.mapDragListener);
            this.mapDragListener = null;
        }

        this.mapShell?.removeEventListener('pointerenter', this.onMapPointerEnter);
        this.mapShell?.removeEventListener('pointerleave', this.onMapPointerLeave);
        this.mapShell?.removeEventListener('wheel', this.onMapWheel, {
            capture: true,
        });
        document.removeEventListener('keydown', this.onEscapeKey);
        this.resumePageScroll();
    }

    onMapPointerEnter() {
        this.pausePageScroll();
    }

    onMapPointerLeave() {
        this.resumePageScroll();
    }

    pausePageScroll() {
        if (this.lenisPausedByMap) {
            return;
        }

        this.lenisPausedByMap = true;
        getLenis()?.stop();
    }

    resumePageScroll() {
        if (!this.lenisPausedByMap) {
            return;
        }

        this.lenisPausedByMap = false;

        if (!document.documentElement.classList.contains('is-scroll-locked')) {
            getLenis()?.start();
        }
    }

    onMapWheel(e) {
        e.preventDefault();
    }

    onEscapeKey(e) {
        if (e.key !== 'Escape') return;
        if (!this.activeMarker) return;
        this.clearActiveMarker();
    }

    waitForMapIdle() {
        return new Promise(resolve => {
            if (!this.mapInstance) {
                resolve();
                return;
            }

            let settled = false;
            const done = () => {
                if (settled) return;
                settled = true;
                resolve();
            };

            google.maps.event.addListenerOnce(this.mapInstance, 'idle', done);
            window.setTimeout(done, 400);
        });
    }

    ensureProjectionOverlay() {
        if (this.projectionOverlay || !this.mapInstance) {
            return this.projectionOverlay;
        }

        class ProjectionOverlay extends google.maps.OverlayView {
            onAdd() {}
            draw() {}
            onRemove() {}
        }

        this.projectionOverlay = new ProjectionOverlay();
        this.projectionOverlay.setMap(this.mapInstance);
        return this.projectionOverlay;
    }

    async centerOnMarker(position) {
        if (!this.mapInstance || !position) return;

        const target =
            position instanceof google.maps.LatLng
                ? position
                : new google.maps.LatLng(position);

        this.ensureProjectionOverlay();
        this.mapInstance.setCenter(target);
        await this.waitForMapIdle();

        // Pin at ~4/5 map height so the bubble fits above it.
        const projection = this.projectionOverlay?.getProjection?.();
        if (!projection) return;

        const point = projection.fromLatLngToContainerPixel(target);
        if (!point) return;

        const mapHeight = this.mapInstance.getDiv()?.clientHeight || 0;
        if (!mapHeight) return;

        const targetPinY = mapHeight * (4 / 5);
        const offsetCenter = projection.fromContainerPixelToLatLng(
            new google.maps.Point(point.x, point.y - (targetPinY - mapHeight / 2))
        );

        if (!offsetCenter) return;

        this.mapInstance.setCenter(offsetCenter);
        await this.waitForMapIdle();
    }

    async onMarkerClick(marker) {
        const item = this.markers.find(m => m.marker === marker);
        if (!item) return;

        if (this.activeMarker === marker) {
            this.clearActiveMarker();
            return;
        }

        if (this.activeMarker) {
            this.setMarkerActiveState(this.activeMarker, false);
        }

        this.activeMarker = marker;
        this.setMarkerActiveState(marker, true);
        this.ignoreMapClickUntil = Date.now() + 500;

        if (typeof this.onMarkerActivate === 'function') {
            this.onMarkerActivate(item.id, item.data);
        }

        await this.centerOnMarker(marker.position);
        if (this.activeMarker !== marker) return;
        this.ignoreMapClickUntil = Date.now() + 500;
        await this.partnerPopup?.open(item.data, marker.position);
    }

    clearActiveMarker() {
        if (this.activeMarker) {
            this.setMarkerActiveState(this.activeMarker, false);
        }

        this.partnerPopup?.close();

        const hadActive = Boolean(this.activeMarker);
        this.activeMarker = null;

        if (hadActive && typeof this.onMarkerDeactivate === 'function') {
            this.onMarkerDeactivate();
        }
    }

    destroy() {
        this.teardownMapListeners();
        this.activeMarker = null;
        this.partnerPopup?.destroy();
        this.partnerPopup = null;
        this.projectionOverlay?.setMap(null);
        this.projectionOverlay = null;
        this.markers = [];
        this.mapInstance = null;
    }

    async goToMarker(id) {
        const item = this.markers.find(m => m.id == id);

        if (!item) return;

        const currentZoom = this.mapInstance.getZoom();
        if (typeof currentZoom === 'number' && currentZoom < 14) {
            this.mapInstance.setZoom(14);
        }

        if (this.activeMarker === item.marker) {
            await this.centerOnMarker(item.marker.position);
            await this.partnerPopup?.open(item.data, item.marker.position);
            return;
        }

        item.marker.click();
    }
}
